import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { config } from 'config';
import axiosRetry from 'axios-retry';
import { AuthenticationService } from 'api/UserManagement';
import { getCookie } from 'utils';
import jwt_decode from 'jwt-decode';

interface AxiosConfig {
  baseURL: string;
  headers?: { [k: string]: string };
  xClientSource?: boolean;
}

export class AxiosClient {
  private readonly client: AxiosInstance;

  private static isExpired({ exp }: { exp?: number }) {
    if (!exp) return true;
    return (Date.now() - exp * 1000) / 1000 > -30;
  }

  private constructor(axiosConfig: AxiosConfig) {
    const headers = axiosConfig.headers || {};
    if (axiosConfig.xClientSource !== false) {
      headers['x-client-source'] = config.X_CLIENT_SOURCE;
    }
    this.client = axios.create({
      ...axiosConfig,
      headers,
    });
    axiosRetry(this.client, {
      retries: 3,
      retryDelay: retryCount => retryCount * 500,
      shouldResetTimeout: true,
      retryCondition: error => {
        return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.code === 'ECONNABORTED';
      },
    });

    this.client.interceptors.request.use(async conf => {
      if (conf.headers?.Authorization) {
        const accessToken = getCookie('access-token');
        const refreshToken = getCookie('refresh-token');
        if (!refreshToken) {
          throw new Error('Missing refresh token');
        }
        const data = accessToken ? (jwt_decode(accessToken) as any) : undefined;
        if (!data || AxiosClient.isExpired(data)) {
          const { token } = await AuthenticationService.authenticateWithRefreshToken(refreshToken);
          conf.headers.Authorization = `Bearer ${token}`;
        }
      }
      return conf;
    });
    this.client.interceptors.response.use(
      r => r,
      error => {
        if (error.config && error.response && (error.response.status === 401 || error.response.status === 403)) {
          const refreshToken = getCookie('refresh-token');
          if (refreshToken) {
            return AuthenticationService.authenticateWithRefreshToken(refreshToken).then(({ token }) => {
              error.config.headers.Authorization = `Bearer ${token}`;
              return AxiosUtils.addDefaultInterceptor(axios).request(error.config);
            });
          }
          return Promise.reject(error.response);
        }
        return Promise.reject(error);
      },
    );
  }

  private getInstance(): AxiosInstance {
    return this.client;
  }

  public static get(axiosConfig: AxiosConfig): AxiosInstance {
    return new AxiosClient(axiosConfig).getInstance();
  }

  public static getAuthorizationHeader(): {
    Authorization: string;
  } {
    return {
      Authorization: `Bearer ${getCookie('access-token')}`,
    };
  }
}

export class AxiosUtils {
  public static addDefaultInterceptor(axiosInstance: AxiosInstance): AxiosInstance {
    axiosInstance.interceptors.response.use(
      r => r,
      error => {
        const { response } = error;
        if (response?.status === 401) {
          return Promise.reject(error.response);
        } else if (error.response) {
          return Promise.reject(error.response);
        }
        return Promise.reject(error);
      },
    );
    return axiosInstance;
  }

  public static enrichTags(r: AxiosResponse | undefined): { statusCode?: string } {
    if (r?.status) {
      return {
        statusCode: r.status.toString(),
      };
    } else {
      return {};
    }
  }
}
